import React, { useState, useRef } from 'react';
import { Box, IconButton, Chip, Button, Tooltip } from '@mui/material';
import { deleteIcon } from '../assets/imgs';

interface DocumentFile {
  id: string;
  name: string;
  file: File | null;
  type: string;
}

interface MultiFileUploadProps {
  files: DocumentFile[];
  onFilesChange: (files: DocumentFile[]) => void;
  fileType: 'ra' | 'wp' | 'ec';
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
}

const MultiFileUpload: React.FC<MultiFileUploadProps> = ({
  files,
  onFilesChange,
  fileType,
  accept = "*",
  multiple = true,
  disabled = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const newFiles: DocumentFile[] = Array.from(selectedFiles).map((file, index) => ({
        id: `temp_${Date.now()}_${index}`,
        name: file.name,
        file: file,
        type: fileType
      }));

      onFilesChange([...files, ...newFiles]);
    }

    // 重置 input 值以允許重複選擇相同檔案
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (fileId: string) => {
    onFilesChange(files.filter(file => file.id !== fileId));
  };

  const handleFileClick = (file: File | null, fileName: string) => {
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      const newTab = window.open(imageUrl);
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 處理檔案名稱截斷的函數
  const truncateFileName = (fileName: string, maxLength: number = 30) => {
    if (fileName.length <= maxLength) {
      return fileName;
    }

    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const maxNameLength = maxLength - (extension ? extension.length + 1 : 0) - 3; // 3 for "..."

    if (nameWithoutExt.length <= maxNameLength) {
      return fileName;
    }

    return `${nameWithoutExt.substring(0, maxNameLength)}...${extension ? '.' + extension : ''}`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        multiple={multiple}
        accept={accept}
        style={{ display: "none" }}
        disabled={disabled}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          onClick={handleUploadClick}
          disabled={disabled}
          variant="outlined"
          size="small"
          sx={{
            minWidth: '120px',
            borderStyle: 'dashed',
            borderColor: '#1976d2',
            color: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: '#f3f8ff'
            }
          }}
        >
          選擇檔案
        </Button>
        <Box sx={{ color: '#666', fontSize: '0.875rem' }}>
          {files.length > 0 ? `已選擇 ${files.length} 個檔案` : '尚未選擇檔案'}
        </Box>
      </Box>

      {files.length > 0 && (
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          p: 2,
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          border: '1px solid #e9ecef'
        }}>
          {files.map((file) => (
            <Box key={file.id} sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: 'white',
              borderRadius: '4px',
              padding: '4px 8px',
              border: '1px solid #dee2e6',
              maxWidth: '300px' // 限制最大寬度
            }}>
              <Tooltip title={file.name} placement="top" arrow>
                <Chip
                  label={truncateFileName(file.name, 25)}
                  onClick={() => handleFileClick(file.file, file.name)}
                  clickable
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{
                    maxWidth: '200px',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }
                  }}
                />
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => removeFile(file.id)}
                disabled={disabled}
                sx={{
                  padding: '4px',
                  '&:hover': {
                    backgroundColor: '#ffebee'
                  }
                }}
              >
                <img src={deleteIcon} style={{ width: 14, height: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MultiFileUpload;